/**
 * CsvBand — file drop zone + template download.
 * Parses CSV (one text per row, # = comment). Fills draft lines → « Vérifier ».
 *
 * Uses parseCsvLine from QueryManager (step 9 moves it to csv.ts; until then
 * we replicate the minimal logic inline).
 *
 * @see B-popup-frontend.md step 5, Files and props § CsvBand
 */
import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';

export interface CsvBandProps {
  /** Appends parsed lines to the CSV draft */
  onLoaded: (lines: string[]) => void;
  onTemplate: () => void;
}

/** Minimal CSV first-cell parser (mirrors QueryManager parseCsvLine). */
function parseFirstCell(line: string): string {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return '';
  if (trimmed.startsWith('"')) {
    const end = trimmed.indexOf('"', 1);
    return end > 0 ? trimmed.slice(1, end).trim() : trimmed.slice(1).trim();
  }
  return trimmed.split(',')[0]?.trim() ?? '';
}

function parseCsv(text: string): string[] {
  const rawLines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');

  const lines: string[] = [];
  for (const raw of rawLines) {
    const cell = parseFirstCell(raw);
    if (!cell) continue;
    // Skip header if first non-empty, non-comment line is "question" (case-insensitive)
    if (lines.length === 0 && cell.toLowerCase() === 'question') continue;
    lines.push(cell);
  }
  return lines;
}

export function CsvBand({ onLoaded, onTemplate }: CsvBandProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string) ?? '';
      // Strip BOM
      const clean = text.startsWith('﻿') ? text.slice(1) : text;
      const lines = parseCsv(clean);
      if (lines.length === 0) {
        setError('Aucune question trouvée dans ce fichier.'); // i18n: promptStudio.csv.empty
      } else {
        onLoaded(lines);
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleTemplate = () => {
    const content = 'question\n# Une question par ligne, dans la première colonne. Les autres colonnes sont ignorées.\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prompts-modele.csv';
    a.click();
    URL.revokeObjectURL(url);
    onTemplate();
  };

  return (
    <Box sx={{ px: 0.5 }}>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragging ? '#7c3aed' : '#d1d5db'}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragging ? 'rgba(124,58,237,.04)' : 'transparent',
          transition: 'border-color .15s, background-color .15s',
          '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,.02)' },
        }}
      >
        <UploadIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
          {/* i18n: promptStudio.csv.drop */}
          Déposez un fichier CSV ici ou{' '}
          <Box component="span" sx={{ color: '#7c3aed', textDecoration: 'underline' }}>
            {/* i18n: promptStudio.csv.browse */}
            parcourir
          </Box>
        </Box>
        <Box sx={{ mt: 0.5, fontSize: 12, color: 'text.disabled' }}>
          {/* i18n: promptStudio.csv.hint */}
          Une question par ligne, dans la première colonne.
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />

      {error && (
        <Box sx={{ mt: 1, fontSize: 12, color: '#b45309' }}>{error}</Box>
      )}

      <Box sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
        <Box
          component="button"
          onClick={(e) => { e.stopPropagation(); handleTemplate(); }}
          sx={{ color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
        >
          {/* i18n: promptStudio.csv.template */}
          Télécharger le modèle
        </Box>
      </Box>
    </Box>
  );
}
